import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { Download, Search, Eye, ClipboardCheck, AlertTriangle, CheckCircle, XCircle, TrendingUp, FileText, Image as ImageIcon, ExternalLink, Plus, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUserContext } from '@/hooks/useUserContext';

interface Inspection {
  id: string;
  area: string;
  inspection_type: string;
  status: string;
  total_score: number;
  max_score: number;
  compliance_percentage: number | null;
  compliance_band: string | null;
  started_at: string;
  completed_at: string | null;
  site_id: string | null;
  inspector_name?: string;
  checklist_name?: string;
  findings_count?: number;
}

interface InspectionFinding {
  id: string;
  severity: string;
  deviation_description: string;
  status: string;
  created_at: string;
  requirement_text?: string;
  inspection_area?: string;
  inspection_id?: string;
  evidence_url?: string;
  ai_reasoning?: string;
}

interface InspectionEvidence {
  id: string;
  evidence_type: string;
  content_text: string;
  content_url: string | null;
  confidence_score: number;
  created_at: string;
}

interface ChecklistItem {
  id: string;
  requirement_text: string;
  is_met: boolean | null; // null = not checked yet
  evidence_url?: string;
}

interface AdminInspectionDataProps {
  siteId?: string | null;
}

const AdminInspectionData = ({ siteId: propSiteId }: AdminInspectionDataProps = {}) => {
  const { companyId, isLoading: contextLoading } = useUserContext();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [findings, setFindings] = useState<InspectionFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState(propSiteId || 'all');
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [activeTab, setActiveTab] = useState('inspections');
  
  // Detail view data
  const [detailEvidence, setDetailEvidence] = useState<InspectionEvidence[]>([]);
  const [detailFindings, setDetailFindings] = useState<InspectionFinding[]>([]);
  const [detailChecklistItems, setDetailChecklistItems] = useState<ChecklistItem[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  // Stats
  const [stats, setStats] = useState({
    totalInspections: 0,
    avgCompliance: 0,
    openFindings: 0,
    criticalFindings: 0,
  });

  useEffect(() => {
    if (propSiteId) {
      setSiteFilter(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    if (!contextLoading && companyId) {
      fetchData();
    }
  }, [siteFilter, contextLoading, companyId]);

  const fetchData = async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      // Fetch inspections with joins - only completed inspections
      let inspectionQuery = supabase
        .from('inspections')
        .select(`
          *,
          inspector:worker_profiles!inspector_id(display_name),
          checklist:inspection_checklists!checklist_id(checklist_name)
        `)
        .eq('company_id', companyId)
        .eq('status', 'completed')
        .order('started_at', { ascending: false });

      if (siteFilter !== 'all') {
        inspectionQuery = inspectionQuery.eq('site_id', siteFilter);
      }

      const { data: inspectionData, error: inspectionError } = await inspectionQuery;
      if (inspectionError) throw inspectionError;

      // Fetch findings count per inspection
      const { data: findingsData, error: findingsError } = await supabase
        .from('inspection_findings')
        .select('inspection_id, severity, id, deviation_description, status, created_at, ai_reasoning, evidence_id');
      
      if (findingsError) throw findingsError;

      // Fetch evidence for findings with photos
      const evidenceIds = (findingsData || []).filter(f => f.evidence_id).map(f => f.evidence_id);
      let evidenceMap: Record<string, string> = {};
      
      if (evidenceIds.length > 0) {
        const { data: evidenceData } = await supabase
          .from('inspection_evidence')
          .select('id, content_url')
          .in('id', evidenceIds);
        
        if (evidenceData) {
          evidenceData.forEach(e => {
            if (e.content_url) {
              evidenceMap[e.id] = e.content_url;
            }
          });
        }
      }

      // Count findings per inspection
      const findingsCounts: Record<string, number> = {};
      (findingsData || []).forEach((f: any) => {
        findingsCounts[f.inspection_id] = (findingsCounts[f.inspection_id] || 0) + 1;
      });

      const transformedInspections: Inspection[] = (inspectionData || []).map((i: any) => ({
        ...i,
        inspector_name: i.inspector?.display_name || 'Unknown',
        checklist_name: i.checklist?.checklist_name || 'N/A',
        findings_count: findingsCounts[i.id] || 0,
      }));

      setInspections(transformedInspections);

      // Fetch all findings with related data
      const { data: allFindings, error: allFindingsError } = await supabase
        .from('inspection_findings')
        .select(`
          *,
          requirement:atomic_requirements!requirement_id(requirement_text),
          inspection:inspections!inspection_id(area)
        `)
        .order('created_at', { ascending: false });
      
      if (allFindingsError) throw allFindingsError;

      const transformedFindings: InspectionFinding[] = (allFindings || []).map((f: any) => ({
        ...f,
        requirement_text: f.requirement?.requirement_text || 'N/A',
        inspection_area: f.inspection?.area || 'N/A',
        evidence_url: f.evidence_id ? evidenceMap[f.evidence_id] : null,
      }));

      setFindings(transformedFindings);

      // Calculate stats
      const completedInspections = transformedInspections.filter(i => i.status === 'completed');
      const avgCompliance = completedInspections.length > 0
        ? completedInspections.reduce((sum, i) => sum + (i.compliance_percentage || 0), 0) / completedInspections.length
        : 0;

      const openFindings = transformedFindings.filter(f => f.status === 'open' || f.status === 'action_assigned').length;
      const criticalFindings = transformedFindings.filter(f => f.severity === 'kritis' && f.status !== 'resolved').length;

      setStats({
        totalInspections: transformedInspections.length,
        avgCompliance: Math.round(avgCompliance),
        openFindings,
        criticalFindings,
      });

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load inspection data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectionDetail = async (inspection: Inspection) => {
    setLoadingDetail(true);
    try {
      // Fetch evidence for this inspection
      const { data: evidenceData } = await supabase
        .from('inspection_evidence')
        .select('*')
        .eq('inspection_id', inspection.id)
        .order('created_at', { ascending: true });
      
      setDetailEvidence(evidenceData || []);

      // Fetch findings for this inspection
      const { data: findingsData } = await supabase
        .from('inspection_findings')
        .select(`
          *,
          requirement:atomic_requirements!requirement_id(requirement_text),
          evidence:inspection_evidence!evidence_id(content_url)
        `)
        .eq('inspection_id', inspection.id)
        .order('severity', { ascending: true });
      
      const transformedFindings = (findingsData || []).map((f: any) => ({
        ...f,
        requirement_text: f.requirement?.requirement_text || 'N/A',
        evidence_url: f.evidence?.content_url || null,
      }));
      
      setDetailFindings(transformedFindings);

      // Fetch the inspection to get the checklist_id
      const { data: inspectionDetails } = await supabase
        .from('inspections')
        .select('checklist_id')
        .eq('id', inspection.id)
        .single();

      // Fetch ALL requirements for this checklist to show complete coverage
      const checklistItems: ChecklistItem[] = [];
      
      if (inspectionDetails?.checklist_id) {
        const { data: allRequirements } = await supabase
          .from('atomic_requirements')
          .select('id, requirement_text, original_item_number')
          .eq('checklist_id', inspectionDetails.checklist_id)
          .order('original_item_number', { ascending: true });

        if (allRequirements && allRequirements.length > 0) {
          // Map each requirement to its status based on evidence/findings
          allRequirements.forEach((req) => {
            // Check if there's evidence for this requirement
            const evidence = (evidenceData || []).find((e: any) => 
              e.requirement_ids?.includes(req.id)
            );
            // Check if there's a finding for this requirement
            const finding = transformedFindings.find((f: any) => 
              f.requirement_id === req.id
            );

            let is_met: boolean | null = null; // null = not checked
            let evidence_url: string | undefined = undefined;

            if (evidence) {
              is_met = evidence.confidence_score >= 0.5;
              evidence_url = evidence.content_url || undefined;
            } else if (finding) {
              is_met = false;
              evidence_url = finding.evidence_url || undefined;
            }

            checklistItems.push({
              id: req.id,
              requirement_text: req.requirement_text,
              is_met,
              evidence_url,
            });
          });
        }
      }
      
      // If no checklist requirements found, fallback to evidence-based logic
      if (checklistItems.length === 0) {
        // Add ALL evidence items - compliant or not
        (evidenceData || []).forEach((e: any) => {
          // Skip evidence that's already linked to a finding (to avoid duplicates)
          const hasLinkedFinding = transformedFindings.some((f: any) => f.evidence_id === e.id);
          if (hasLinkedFinding) return;
          
          const isCompliant = e.confidence_score >= 0.5;
          checklistItems.push({
            id: e.id,
            requirement_text: e.content_text || (isCompliant ? 'Compliant observation' : 'Non-conformance'),
            is_met: isCompliant,
            evidence_url: e.content_url,
          });
        });
        
        // Add non-compliant items from findings
        transformedFindings.forEach((f: any) => {
          checklistItems.push({
            id: f.id,
            requirement_text: f.deviation_description || 'Non-conformance',
            is_met: false,
            evidence_url: f.evidence_url,
          });
        });
      }

      setDetailChecklistItems(checklistItems);
      
      // Also add findings from evidence with low confidence that aren't already in findings
      const additionalFindings: any[] = [];
      (evidenceData || []).forEach((e: any) => {
        if (e.confidence_score < 0.5) {
          // Check if there's already a finding for this evidence
          const existingFinding = transformedFindings.find((f: any) => f.evidence_id === e.id);
          if (!existingFinding) {
            additionalFindings.push({
              id: e.id,
              deviation_description: e.content_text || 'Non-conformance',
              severity: 'sedang',
              status: 'open',
              evidence_url: e.content_url,
              requirement_text: e.content_text || 'N/A',
            });
          }
        }
      });
      
      // Merge additional findings with existing ones
      if (additionalFindings.length > 0) {
        setDetailFindings([...transformedFindings, ...additionalFindings]);
      }

    } catch (error) {
      console.error('Error fetching inspection detail:', error);
      toast.error('Failed to load inspection details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleViewDetail = async (inspection: Inspection) => {
    setSelectedInspection(inspection);
    setShowDetail(true);
    await fetchInspectionDetail(inspection);
  };

  const handleCreateAction = async (finding: InspectionFinding) => {
    try {
      // Get user's company and site
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('company_id, site_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) return;

      // Create supervisor task
      const { error } = await supabase
        .from('supervisor_tasks')
        .insert({
          company_id: profile.company_id,
          site_id: profile.site_id,
          source_type: 'inspection_finding',
          source_id: finding.id,
          title: `[INSPEKSI] ${finding.severity.toUpperCase()}: ${(finding.deviation_description || 'Temuan').substring(0, 50)}`,
          description: `Temuan inspeksi dari area ${finding.inspection_area}.\n\nDeskripsi: ${finding.deviation_description || 'N/A'}\nTindakan yang disarankan: ${finding.ai_reasoning || 'Review dan tindak lanjut'}`,
          priority: finding.severity === 'kritis' ? 'high' : finding.severity === 'tinggi' ? 'medium' : 'low',
          status: 'pending',
          verification_required: true,
        });

      if (error) throw error;

      toast.success('Action created and added to queue');
      
      // Update finding status
      await supabase
        .from('inspection_findings')
        .update({ status: 'action_assigned' })
        .eq('id', finding.id);

      fetchData();

    } catch (error) {
      console.error('Error creating action:', error);
      toast.error('Failed to create action');
    }
  };

  const filteredInspections = inspections.filter(i => {
    const matchesSearch = i.area.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.inspector_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || i.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredFindings = findings.filter(f => {
    const matchesSearch = f.deviation_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.requirement_text?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getComplianceBadge = (percentage: number | null, band: string | null) => {
    if (percentage === null) return <Badge variant="outline">N/A</Badge>;
    
    const colors: Record<string, string> = {
      excellent: 'bg-green-500',
      good: 'bg-blue-500',
      needs_improvement: 'bg-yellow-500',
      critical: 'bg-red-500',
    };
    
    return (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${colors[band || 'needs_improvement']}`} />
        <span className="font-medium">{percentage}%</span>
      </div>
    );
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      kritis: 'destructive',
      tinggi: 'default',
      sedang: 'secondary',
      rendah: 'outline',
    };
    return <Badge variant={variants[severity] || 'default'}>{severity.toUpperCase()}</Badge>;
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Date', 'Inspector', 'Area', 'Type', 'Status', 'Score', 'Max Score', 'Compliance %', 'Findings'];
    const csvData = filteredInspections.map(i => [
      i.id,
      new Date(i.started_at).toLocaleString(),
      i.inspector_name,
      i.area,
      i.inspection_type,
      i.status,
      i.total_score,
      i.max_score,
      i.compliance_percentage || 'N/A',
      i.findings_count,
    ]);

    const csvContent = [headers, ...csvData].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inspection-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Data exported successfully');
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Inspections</p>
                <p className="text-2xl font-bold">{stats.totalInspections}</p>
              </div>
              <ClipboardCheck className="h-8 w-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Compliance</p>
                <p className="text-2xl font-bold">{stats.avgCompliance}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500 opacity-80" />
            </div>
            <Progress value={stats.avgCompliance} className="mt-2" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Findings</p>
                <p className="text-2xl font-bold">{stats.openFindings}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical Issues</p>
                <p className="text-2xl font-bold text-destructive">{stats.criticalFindings}</p>
              </div>
              <XCircle className="h-8 w-8 text-destructive opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Inspection Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <TabsList>
                <TabsTrigger value="inspections">Inspections</TabsTrigger>
                <TabsTrigger value="findings">Findings</TabsTrigger>
              </TabsList>
              
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {!propSiteId && <SiteFilter value={siteFilter} onChange={setSiteFilter} className="w-full md:w-48" />}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={exportToCSV} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>

            <TabsContent value="inspections">
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Inspector</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead>Checklist</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Compliance</TableHead>
                      <TableHead>Findings</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8">Loading...</TableCell>
                      </TableRow>
                    ) : filteredInspections.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                          No inspections found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInspections.map((inspection) => (
                        <TableRow key={inspection.id}>
                          <TableCell className="whitespace-nowrap">
                            {new Date(inspection.started_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{inspection.inspector_name}</TableCell>
                          <TableCell>{inspection.area}</TableCell>
                          <TableCell className="max-w-[150px] truncate">{inspection.checklist_name}</TableCell>
                          <TableCell className="capitalize">{inspection.inspection_type}</TableCell>
                          <TableCell>
                            <Badge variant={inspection.status === 'completed' ? 'default' : 'secondary'}>
                              {inspection.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{inspection.total_score}/{inspection.max_score}</TableCell>
                          <TableCell>
                            {getComplianceBadge(inspection.compliance_percentage, inspection.compliance_band)}
                          </TableCell>
                          <TableCell>
                            {inspection.findings_count && inspection.findings_count > 0 ? (
                              <Badge variant="outline" className="text-destructive border-destructive">
                                {inspection.findings_count}
                              </Badge>
                            ) : (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetail(inspection)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="findings">
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead>Photo</TableHead>
                      <TableHead>Deviation</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                      </TableRow>
                    ) : filteredFindings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No findings found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredFindings.map((finding) => (
                        <TableRow key={finding.id}>
                          <TableCell className="whitespace-nowrap">
                            {new Date(finding.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{finding.inspection_area}</TableCell>
                          <TableCell>
                            {finding.evidence_url ? (
                              <button
                                onClick={() => setSelectedPhoto(finding.evidence_url!)}
                                className="w-10 h-10 rounded border overflow-hidden hover:opacity-80 transition-opacity"
                              >
                                <img 
                                  src={finding.evidence_url} 
                                  alt="Evidence" 
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ) : (
                              <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center">
                                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[250px] truncate">{finding.deviation_description}</TableCell>
                          <TableCell>{getSeverityBadge(finding.severity)}</TableCell>
                          <TableCell>
                            <Badge variant={finding.status === 'resolved' ? 'default' : 'outline'}>
                              {finding.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {finding.status === 'open' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCreateAction(finding)}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Create Action
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Inspection Details - {selectedInspection?.area}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            {loadingDetail ? (
              <div className="text-center py-8">Loading details...</div>
            ) : selectedInspection && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Inspector</p>
                    <p className="font-medium">{selectedInspection.inspector_name}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium capitalize">{selectedInspection.inspection_type}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Score</p>
                    <p className="font-bold text-lg">{selectedInspection.total_score}/{selectedInspection.max_score}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Compliance</p>
                    <div className="flex items-center gap-2">
                      <Progress value={selectedInspection.compliance_percentage || 0} className="flex-1 h-2" />
                      <span className="font-bold">{selectedInspection.compliance_percentage || 0}%</span>
                    </div>
                  </div>
                </div>

                {/* Checklist Breakdown */}
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4" />
                    Checklist Items ({detailChecklistItems.filter(i => i.is_met === true).length}/{detailChecklistItems.length} Met, {detailChecklistItems.filter(i => i.is_met === null).length} Unchecked)
                  </h3>
                  <div className="space-y-2">
                    {detailChecklistItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No checklist items recorded</p>
                    ) : (
                      detailChecklistItems.map((item) => (
                        <div 
                          key={item.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${
                            item.is_met === true 
                              ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' 
                              : item.is_met === false 
                                ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800' 
                                : 'bg-muted/50 border-muted-foreground/20'
                          }`}
                        >
                          {item.is_met === true ? (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : item.is_met === false ? (
                            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <MinusCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm">{item.requirement_text}</p>
                            {item.is_met === null && (
                              <p className="text-xs text-muted-foreground mt-0.5">Not checked</p>
                            )}
                          </div>
                          {item.evidence_url && (
                            <button
                              onClick={() => setSelectedPhoto(item.evidence_url!)}
                              className="w-12 h-12 rounded border overflow-hidden hover:opacity-80 transition-opacity flex-shrink-0"
                            >
                              <img 
                                src={item.evidence_url} 
                                alt="Evidence" 
                                className="w-full h-full object-cover"
                              />
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Findings Section */}
                {detailFindings.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      Findings ({detailFindings.length})
                    </h3>
                    <div className="space-y-3">
                      {detailFindings.map((finding) => (
                        <div key={finding.id} className="p-4 border rounded-lg bg-destructive/5 border-destructive/20">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {getSeverityBadge(finding.severity)}
                                <Badge variant="outline">{finding.status}</Badge>
                              </div>
                              <p className="text-sm font-medium">{finding.deviation_description}</p>
                              {finding.ai_reasoning && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Suggested: {finding.ai_reasoning}
                                </p>
                              )}
                            </div>
                            {finding.evidence_url && (
                              <button
                                onClick={() => setSelectedPhoto(finding.evidence_url!)}
                                className="w-16 h-16 rounded border overflow-hidden hover:opacity-80 transition-opacity flex-shrink-0"
                              >
                                <img 
                                  src={finding.evidence_url} 
                                  alt="Evidence" 
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            )}
                          </div>
                          {finding.status === 'open' && (
                            <div className="mt-3 pt-3 border-t">
                              <Button 
                                size="sm" 
                                onClick={() => handleCreateAction(finding)}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Create Action in Queue
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evidence Gallery */}
                {detailEvidence.filter(e => e.content_url).length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      Evidence Gallery
                    </h3>
                    <div className="grid grid-cols-4 gap-3">
                      {detailEvidence.filter(e => e.content_url).map((evidence) => (
                        <button
                          key={evidence.id}
                          onClick={() => setSelectedPhoto(evidence.content_url!)}
                          className="aspect-square rounded-lg border overflow-hidden hover:opacity-80 transition-opacity"
                        >
                          <img 
                            src={evidence.content_url!} 
                            alt={evidence.content_text || 'Evidence'} 
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="text-sm text-muted-foreground space-y-1 pt-4 border-t">
                  <p>Started: {new Date(selectedInspection.started_at).toLocaleString()}</p>
                  {selectedInspection.completed_at && (
                    <p>Completed: {new Date(selectedInspection.completed_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-3xl p-2">
          {selectedPhoto && (
            <img 
              src={selectedPhoto} 
              alt="Evidence" 
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInspectionData;
